import { GenericLazyString, gettext } from '@eventbrite/i18n';
import React, { useState } from 'react';
import { useLocationContext } from '../../context';

import { DestinationEvent } from '@eventbrite/event-renderer';
import { Organizer } from '../../components/OrganizersSection';
import { Places } from '../NearbyCities';
import { Trending } from '../TrendingSearches';

import {
    Accordion,
    AccordionContent,
    AccordionHeader,
} from '@eventbrite/eds-accordion';
import { Divider } from '@eventbrite/eds-divider';
import './AccordionSection.scss';

export type FAQItemProps = {
    questionText: string | GenericLazyString;
    answerText: string | GenericLazyString;
    children?: React.ReactNode;
    sectionData?: {
        name: string;
        url: string;
        id: string;
    }[];
};

export const FAQItem: React.FunctionComponent<FAQItemProps> = (props) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const handleMouseDown = (e: React.MouseEvent) => {
        setIsExpanded(false);
        // Prevent the focus event from firing
        e.preventDefault();
    };

    const handleFocus = () => {
        setIsExpanded(true);
    };

    const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setIsExpanded(false);
        }
    };
    return (
        <div
            tabIndex={0}
            onMouseDown={handleMouseDown}
            onFocus={handleFocus}
            onBlur={handleBlur}
            className="ttd_faq-section-container"
        >
            <Accordion isExpanded={isExpanded}>
                <div
                    itemScope
                    itemProp="mainEntity"
                    itemType="https://schema.org/Question"
                >
                    <AccordionHeader>
                        <div
                            className="ttd_faq-section_accordion-header"
                            itemProp="name"
                        >
                            {props.questionText}
                        </div>
                    </AccordionHeader>
                    <AccordionContent>
                        <div className="ttd_faq-section_accordion-content">
                            <div
                                itemScope
                                itemProp="acceptedAnswer"
                                itemType="https://schema.org/Answer"
                            >
                                <div
                                    className="ttd_faq-section_accordion-answer"
                                    itemProp="text"
                                >
                                    {props.answerText}
                                    <div className="ttd_faq-section_accordion-ul">
                                        {props.children ? (
                                            <>{props.children}</>
                                        ) : (
                                            <>
                                                {props.sectionData &&
                                                    props.sectionData.map(
                                                        ({ name, url, id }) => (
                                                            <li
                                                                key={
                                                                    'listItem' +
                                                                    id
                                                                }
                                                            >
                                                                <a
                                                                    key={
                                                                        'linkText' +
                                                                        id
                                                                    }
                                                                    href={url}
                                                                >
                                                                    {name}
                                                                </a>{' '}
                                                                <br />
                                                            </li>
                                                        ),
                                                    )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </AccordionContent>
                </div>
            </Accordion>
            <Divider />
        </div>
    );
};

interface TopEventsSectionProps {
    data: DestinationEvent[];
}

export const TopEventsSection: React.FunctionComponent<
    TopEventsSectionProps
> = (props) => {
    // grab the first 4 entries in sectionData.
    const { shortPlaceName } = useLocationContext();

    let questionText: GenericLazyString | string = '';
    let answerText: GenericLazyString | string = '';

    if (shortPlaceName === 'Online') {
        questionText = gettext('What are the best things to do Online?');
        answerText = gettext('The best things to do Online are:');
    } else {
        questionText = gettext(
            'What are the best things to do in %(shortPlaceName)s?',
            { shortPlaceName },
        );
        answerText = gettext(
            'The best things to do in %(shortPlaceName)s are:',
            { shortPlaceName },
        );
    }

    const sectionData = props.data.slice(0, 4);

    return (
        <FAQItem
            questionText={questionText}
            answerText={answerText}
            sectionData={sectionData}
        />
    );
};

interface TopOrganizersSectionProps {
    data: Organizer[];
}

export const TopOrganizersSection: React.FunctionComponent<
    TopOrganizersSectionProps
> = (props) => {
    // grab the first 4 entries in sectionData.
    const { shortPlaceName } = useLocationContext();
    // clarify copy on Top Organizers
    const questionText = gettext(
        'Who are the top organizers in %(shortPlaceName)s?',
        { shortPlaceName: shortPlaceName },
    );
    const answerText = gettext(
        'The top organizers in %(shortPlaceName)s are:',
        { shortPlaceName: shortPlaceName },
    );
    const sectionData = props.data.slice(0, 4);

    return (
        <FAQItem
            questionText={questionText}
            answerText={answerText}
            sectionData={sectionData}
        />
    );
};

interface NearbyCitiesSectionProps {
    data: Places;
}

export const NearbyCitiesSection: React.FunctionComponent<
    NearbyCitiesSectionProps
> = (props) => {
    // grab the first 4 entries in sectionData.
    const { shortPlaceName } = useLocationContext();
    const questionText = gettext(
        'What are the best cities to visit near %(shortPlaceName)s?',
        { shortPlaceName: shortPlaceName },
    );
    const answerText = gettext(
        'The best cities to visit near %(shortPlaceName)s are:',
        { shortPlaceName: shortPlaceName },
    );
    const sectionData = props.data.slice(0, 4);

    return (
        <FAQItem
            questionText={questionText}
            answerText={answerText}
            sectionData={sectionData}
        />
    );
};

interface TrendingSearchesSectionProps {
    data: Trending;
}

export const TrendingSearchesSection: React.FunctionComponent<
    TrendingSearchesSectionProps
> = (props) => {
    // grab the first 4 entries in sectionData.
    const { shortPlaceName } = useLocationContext();

    let questionText: GenericLazyString | string = '';
    let answerText: GenericLazyString | string = '';

    if (shortPlaceName === 'Online') {
        questionText = gettext(
            'What are the most popular things to do Online?',
        );
        answerText = gettext(
            'The most popular things to do Online according to Eventbrite users are:',
        );
    } else {
        questionText = gettext(
            'What are the most popular things to do in %(shortPlaceName)s?',
            { shortPlaceName },
        );
        answerText = gettext(
            'The most popular things to do in %(shortPlaceName)s according to Eventbrite users are:',
            { shortPlaceName },
        );
    }

    const sectionData = props.data.slice(0, 4);

    return (
        <FAQItem questionText={questionText} answerText={answerText}>
            {sectionData.map(({ term, search_path, id }) => (
                <li key={'listItem' + id}>
                    <a key={'linkText' + id} href={search_path}>
                        {term}
                    </a>{' '}
                    <br />
                </li>
            ))}
        </FAQItem>
    );
};
